<template>
  <v-app-bar
    absolute
    app
    color="transparent"
    flat
    height="75"
    style="width: auto"
  >
    <v-btn v-if="showExpandButton" fab small @click="expandMenu" class="ml-4">
      <v-icon>mdi-dots-vertical</v-icon>
    </v-btn>
    <router-link to="/">
      <img
        :src="organizationLogo"
        :alt="user.OrganizationName"
        style="max-height: 67px"
        :class="showExpandButton ? 'mt-2' : 'mt-2 ml-3'"
      />
    </router-link>
    <base-multiselect
      align-self="center"
      class="mt-2 ml-4"
      :ref="contentTypesMultiselectKey"
      :placeholder="$t('SelectItem')"
      :items="userContentTypes"
      @on-selection-change="onContentTypeSelected"
      appendOuterIcon="mdi-folder-open-outline"
      cssStyle="max-width: 20%"
    />
    <v-text-field
      align-self="center"
      :label="$t('Search')"
      v-model="searchText"
      color="primary"
      hide-details
      class="ml-4"
      :error-messages="searchErrors"
      @keydown.enter="searchDocuments"
    >
      <template v-slot:append-outer>
        <v-btn
          class="mt-n2"
          fab
          small
          @click="searchDocuments"
          :loading="searchLoading"
        >
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <div class="mx-3" align-self="center">
      <v-menu
        bottom
        left
        offset-y
        origin="top right"
        :key="notifications.length"
      >
        <template v-slot:activator="{ attrs, on }">
          <v-btn class="ml-2" min-width="0" text v-bind="attrs" v-on="on">
            <v-badge :color="notificationColor" overlap bordered>
              <template v-slot:badge>
                <span>{{ notifications.length }}</span>
              </template>

              <v-icon>mdi-bell</v-icon>
            </v-badge>
          </v-btn>
        </template>

        <v-list flat tile class="pa-0 ma-0">
          <UserAlerts
            :alerts="notifications"
            @on-alert-removed="getUserAlerts"
          />
        </v-list>
      </v-menu>
    </div>
    <v-menu bottom left min-width="200" offset-y origin="top right">
      <template v-slot:activator="{ attrs, on }">
        <v-btn min-width="0" text v-bind="attrs" v-on="on">
          {{ currentUserName }}
          <v-icon right>mdi-account</v-icon>
        </v-btn>
      </template>

      <v-list :tile="false" flat nav>
        <v-list-item @click.prevent="editProfile" class="tile">
          <v-list-item-title v-text="$t('EditProfile')" />
        </v-list-item>

        <v-divider class="mb-2 mt-2" />
        <v-list-item @click.prevent="logout" class="tile">
          <v-list-item-title v-text="$t('Logout')" />
        </v-list-item>
      </v-list>
    </v-menu>
    <UserModal :ref="profileModalKey" @on-item-saved="onProfileSaved" />
  </v-app-bar>
</template>

<script>
import UserModal from "@/components/administration/user/UserModal";
import SearchType from "@/ecm/variables/SearchType";
import UserAlerts from "@/components/core/UserAlerts";
import AlertService from "@/services/AlertService";
import Notification from "@/ecm/models/UserAlert";
import BrowserAlert from "@/ecm/common/BrowserAlert";
import AlertType from "@/ecm/variables/AlertType";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { mapGetters } from "vuex";

export default {
  name: "AppCoreAppBar",

  components: {
    UserModal,
    UserAlerts,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    if (this.token) {
      this.connection = $.hubConnection(this.apiUrl);

      this.connection.qs = { access_token: this.token };

      BrowserAlert.requestPermission();
      var notificationHub = this.connection.createHubProxy("notificationHub");

      var self = this;
      notificationHub.on("notify", (notifications) => {
        let notificationBackup = this.notifications;
        this.notifications = notifications.map(function (dto) {
          let notification = new Notification(
            dto,
            self.title(dto.Type),
            self.description(dto.Type, dto.ContentTypeName)
          );
          return notification;
        });
        if (notificationBackup.length != 0) {
          this.notifications.forEach((newArray) => {
            notificationBackup.forEach((backup) => {
              if (
                newArray.type == backup.type &&
                newArray.contentTypeIds.toString() ==
                  backup.contentTypeIds.toString() &&
                Math.max(...backup.alertIds) < Math.max(...newArray.alertIds)
              ) {
                BrowserAlert.show(newArray);
              }
            });
          });
        } else {
          this.notifications.forEach((newArray) => {
            BrowserAlert.show(newArray);
          });
        }
      });

      this.connection.start();
    }
  },
  destroyed() {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
    }
  },
  data: () => ({
    connection: null,
    searchText: "",
    searchLoading: false,
    profileModalKey: "profileModal",
    notifications: [],
    contentTypesMultiselectKey: "ctSearchMultiselectKey",
    selectedContentTypes: [],
  }),
  mixins: [validationMixin],
  validations: {
    searchText: { required },
  },
  created() {
    this.getUserAlerts();
    this.$v.$reset();
  },
  computed: {
    ...mapGetters({
      user: "account/user",
      apiUrl: "account/apiUrl",
      token: "account/token",
      userContentTypes: "account/userContentTypes",
    }),
    currentUserName() {
      return this.user.Surname + "," + this.user.Name;
    },
    showExpandButton() {
      return this.$vuetify.breakpoint.mobile;
    },
    searchErrors() {
      const errors = [];
      if (!this.$v.searchText.$dirty) return errors;
      !this.$v.searchText.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    profileModal() {
      return this.$refs[this.profileModalKey];
    },
    userCultureCode() {
      if (this.user) {
        return this.user.CultureCode;
      } else {
        return "en";
      }
    },
    organizationLogo() {
      if (this.user.IsAdmin) {
        return "/static/logo2.png";
      } else {
        return `${this.apiUrl}/api/organization/logo/${this.user.OrganizationID}?access_token=${this.token}`;
      }
    },
    notificationColor() {
      return this.notifications.length > 0 ? "red" : "primary";
    },
  },
  watch: {
    userCultureCode: {
      immediate: true,
      handler(val) {
        this.$vuetify.lang.current = val;
      },
    },
    $route(to, from) {
      if (to.name != "Search") {
        this.searchText = "";
      }
    },
  },

  methods: {
    onContentTypeSelected(contentTypeIDs) {
      this.selectedContentTypes = contentTypeIDs;
    },
    searchDocuments() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.$v.$reset();
      this.searchLoading = true;

      var pattern = {
        contentTypeIDs: this.selectedContentTypes.join(),
        searchPattern: this.searchText,
      };

      var route = `/search/${SearchType.FullText}/${encodeURIComponent(
        JSON.stringify(pattern)
      )}`;

      this.$router.push(route);
      // this.searchText = "";
      this.searchLoading = false;
    },
    editProfile() {
      this.profileModal.edit(this.user);
    },
    onProfileSaved() {
      Promise.all([
        this.$store.dispatch("account/getUser"),
        this.$store.dispatch("account/getUserMenu"),
        this.$store.dispatch("account/getLocalization"),
        this.$store.dispatch("account/getOperators"),
        this.$store.dispatch("account/getStandardFields"),
      ]).finally(() => {
        location.reload();
      });
    },
    logout() {
      window.getApp.$emit("APP_LOGOUT");
    },
    expandMenu() {
      this.$store.dispatch("theme/setDrawer", true);
    },
    getUserAlerts() {
      if (this.token) {
        AlertService.getUserList().then((data) => {
          this.notifications = data.map(function (dto) {
            return new Notification(dto);
          });
        });
      }
    },
    title(alert) {
      switch (alert) {
        case AlertType.ContentType:
          return `${this.$t("ContentTypeAlert")}`;
        case AlertType.UserGenerated:
          return `${this.$t("UserAlert")}`;
        case AlertType.FlowStateChange:
        case AlertType.FlowStateExpired:
          return `${this.$t("FlowAlert")}`;
      }
    },
    description(alert, description) {
      switch (alert) {
        case AlertType.ContentType:
          return `${this.$t("AlertContentTypeDescription")}: ${description}`;
        case AlertType.UserGenerated:
          return `${this.$t("AlertUserGeneratedDescription")}: ${description}`;
        case AlertType.FlowStateChange:
          return `${this.$t("AlertStateChangeDescription")}: ${description}`;
        case AlertType.FlowStateExpired:
          return `${this.$t("AlertStateExpiredDescription")}: ${description}`;
      }
    },
  },
};
</script>
<style>
.tile:hover {
  background: lightgrey;
}
.v-toolbar__content {
  padding-left: 0px !important;
}
</style>